import { create } from 'zustand'
import { AtlasSlice, createAtlasSlice } from './slices'
import { BonusModalConfig, BuyBonusFeature } from 'game-ui-types'

interface BuyBonusStore extends AtlasSlice {
    buyBonusConfig: {
        maxBet: number
        layout: BonusModalConfig
        choiceCount: number
        features: BuyBonusFeature[]
    }
    setBuyBonusConfig: (buyBonusConfig: {
        maxBet: number
        choiceCount: number
        layout: BonusModalConfig
        features: BuyBonusFeature[]
    }) => void
    isMultiChoiceToggled: boolean
    setMultiChoiceToggle: (isToggled: boolean) => void
    showModal: boolean
    setShowModal: (value: boolean) => void
    currentFeatureMultiplier: number
    updateCurrentFeatureMultiplier: (multiplier: number) => void
    selectedFeature: BuyBonusFeature | null
    setSelectedFeature: (feature: BuyBonusFeature | null) => void
    hasGuaranteedWin: boolean
    setHasGuaranteedWin: (hasGuaranteedWin: boolean) => void
    selectedFeatureIndex: number
    setSelectedFeatureIndex: (index: number) => void
    isGuaranteedWinEnabled: boolean
    setIsGuaranteedWinEnabled: (value: boolean) => void
    isForceGuaranteedWinEnabled: boolean
    setIsForceGuaranteedWinEnabled: (value: boolean) => void
    hasChancePlus: boolean
    setHasChancePlus: (value: boolean) => void
}

export const useBuyBonusStore = create<BuyBonusStore>((set, get) => ({
    ...createAtlasSlice(set, get),
    buyBonusConfig: {
        maxBet: 800,
        choiceCount: 1,
        layout: {
            modalWidth: '',
            headerColors: [],
            bodyColors: [],
            footerColors: [],
            bundleContainerBackground: [],
            allSymbolsBackgroundColors: [],
            moneyColor: '',
            labelColor: '',
        },
        features: [
            {
                name: 'BUY_BONUS',
                label: 'Bonus',
                labelFontSize: '36px',
                multiplier: 20,
                guaranteedMultiplier: 0,
                guaranteedName: 'BUY_1_WILD_GUARANTEE',
                featureGroup: 1,
            },
        ],
    },
    setBuyBonusConfig: (buyBonusConfig: {
        maxBet: number
        choiceCount: number
        layout: BonusModalConfig
        features: BuyBonusFeature[]
    }) => {
        set((state) => {
            const mergedLayout = {
                ...state.buyBonusConfig.layout,
                ...buyBonusConfig.layout,
            }

            const mergedFeatures = buyBonusConfig.features ?? state.buyBonusConfig.features

            return {
                buyBonusConfig: {
                    maxBet: buyBonusConfig.maxBet ?? state.buyBonusConfig.maxBet,
                    choiceCount: buyBonusConfig.choiceCount ?? state.buyBonusConfig.choiceCount,
                    layout: mergedLayout,
                    features: mergedFeatures,
                },
                selectedFeature: mergedFeatures[0] ?? null,
            }
        })
    },
    isMultiChoiceToggled: false,
    setMultiChoiceToggle: (isToggled: boolean) => {
        set(() => ({ isMultiChoiceToggled: isToggled }))
    },
    currentFeatureMultiplier: 1,
    updateCurrentFeatureMultiplier: (multiplier: number) => {
        set({ currentFeatureMultiplier: multiplier })
    },
    selectedFeature: null,
    setSelectedFeature: (feature: BuyBonusFeature | null) => {
        // Keep selectedFeatureIndex in sync with the selected feature.
        set((state) => {
            const idx = feature ? state.buyBonusConfig.features.findIndex((f) => f.name === feature.name) : 0
            return {
                selectedFeature: feature,
                selectedFeatureIndex: idx >= 0 ? idx : 0,
            }
        })
    },
    hasGuaranteedWin: false,
    setHasGuaranteedWin: (hasGuaranteedWin: boolean) => {
        set({ hasGuaranteedWin })
    },
    selectedFeatureIndex: 0,
    setSelectedFeatureIndex: (index: number) => {
        // Keep selectedFeature in sync with the index.
        set((state) => {
            const feature = state.buyBonusConfig.features[index] ?? null
            return {
                selectedFeatureIndex: index,
                selectedFeature: feature,
            }
        })
    },
    showModal: false,
    setShowModal: (value) => set({ showModal: value }),
    isGuaranteedWinEnabled: false,
    setIsGuaranteedWinEnabled: (value) => set({ isGuaranteedWinEnabled: value }),
    isForceGuaranteedWinEnabled: false,
    setIsForceGuaranteedWinEnabled: (value) => set({ isForceGuaranteedWinEnabled: value }),
    hasChancePlus: false,
    setHasChancePlus: (value) => set({ hasChancePlus: value }),
}))
